import React, { useState } from "react";
import { LuUpload, LuX } from "react-icons/lu";
import { Badge, cn, notify } from "../../components/ui";
import { t } from "../../i18n";
import { acceptPdfs, formatFileSize } from "./email";
import { Section } from "./parts";

interface PdfDropzoneProps {
  files: File[];
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
  error?: string;
}

/** Área de PDFs: input nativo (clique) ou arrastar e soltar, até 10 MB por arquivo. */
export const PdfDropzone: React.FC<PdfDropzoneProps> = ({ files, onAdd, onRemove, error }) => {
  const [over, setOver] = useState(false);

  const add = (chosen: File[]) => {
    const { accepted, warnings } = acceptPdfs(chosen, files);
    warnings.forEach((title) => notify({ tone: "warning", title }));
    if (accepted.length > 0) onAdd(accepted);
  };

  return (
    <Section title={<>{t("sharing.email.pdfs")} {files.length > 0 && <Badge tone="primary">{files.length}</Badge>}</>}>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          add(Array.from(e.dataTransfer.files));
        }}
        className={cn(
          "relative flex cursor-pointer flex-col items-center gap-1.5 rounded-card border-2 border-dashed px-4 py-5 text-center text-fg-muted transition-colors focus-within:shadow-focus",
          over ? "border-primary bg-primary-soft text-primary" : "border-primary-line bg-primary-tint hover:border-primary"
        )}
      >
        <input
          type="file"
          accept="application/pdf,.pdf"
          multiple
          className="sr-only"
          onChange={(e) => {
            add(Array.from(e.target.files ?? []));
            e.target.value = ""; // permite escolher o mesmo arquivo depois de removê-lo
          }}
        />
        <LuUpload size={22} aria-hidden />
        <strong className="text-label font-semibold text-fg">{t(files.length ? "sharing.email.dropMore" : "sharing.email.dropTitle")}</strong>
        <span className="text-caption">{t("sharing.email.dropHint")}</span>
      </label>

      {files.length > 0 && <FileList files={files} onRemove={onRemove} />}
      {error && <p className="text-caption text-danger">{error}</p>}
    </Section>
  );
};

/** Lista dos PDFs anexados; sem `onRemove`, só leitura (revisão). */
export const FileList: React.FC<{ files: File[]; onRemove?: (index: number) => void }> = ({ files, onRemove }) => (
  <ul className="flex flex-col gap-2">
    {files.map((file, index) => (
      <li key={`${file.name}-${file.size}-${index}`} className="flex items-center gap-3 rounded-control border border-line bg-surface px-3 py-2.5">
        <span className="grid h-9 w-8 shrink-0 place-items-center rounded-md bg-danger text-badge font-extrabold tracking-wide text-white" aria-hidden>
          PDF
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <strong className="truncate text-label font-semibold text-fg">{file.name}</strong>
          <span className="text-caption text-fg-muted">{formatFileSize(file.size)}</span>
        </span>
        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            aria-label={t("sharing.email.removeFile", { name: file.name })}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-fg-muted hover:bg-danger-soft hover:text-danger focus-visible:outline-none focus-visible:shadow-focus"
          >
            <LuX size={16} aria-hidden />
          </button>
        )}
      </li>
    ))}
  </ul>
);
